import { useEffect, useMemo, useState } from "react"
import { LexicalText } from "@/schema"
import type { SearchResult } from "@/schema/search"
import { filterUsersForUserReferenceField } from "@/utils/custom-field-users"
import { DateInput } from "@ui/forms/inputs/date-input"
import { MultiSelectInput } from "@ui/forms/inputs/multi-select-input"
import { TextInput } from "@ui/forms/inputs/text-input"
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Switch } from "@ui/shad"
import { LoaderCircle } from "lucide-react"

import { useFetchUsers } from "@/hooks/api/users"
import { CrossReferenceChip } from "@/components/cross-references/cross-reference-chip"
import { SearchDialog } from "@/components/dialogs/search-dialog"
import { Editor } from "@/components/lexical"
import { UserAvatar } from "@/components/users/user-avatar"

import { allowedRelationshipSearchTypes, SEARCH_TYPE_TO_BACKEND_TYPE } from "./relationship-targets"
import { SelectOptionDot } from "./select-option"
import { getCrossReferenceData, type CrossReferenceValue, type CustomFieldFormInputProps } from "./types"

interface CrossReferenceFormFieldProps {
  customField: CustomFieldFormInputProps["customField"]
  value: CrossReferenceValue | null
  onSave: (value: unknown) => void
  isLoading: boolean
}

function CrossReferenceFormField({ customField, value, onSave, isLoading }: CrossReferenceFormFieldProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [pendingValue, setPendingValue] = useState<CrossReferenceValue | null>(value)

  const currentValue = pendingValue ? getCrossReferenceData(pendingValue) : null

  useEffect(() => {
    setPendingValue(value)
  }, [value])

  const handleSelect = (item: SearchResult) => {
    const nextValue: CrossReferenceValue = {
      type: SEARCH_TYPE_TO_BACKEND_TYPE[item.type],
      id: item.id,
      label: item.display_label,
    }

    setPendingValue(nextValue)
    // Map search result type to backend type (e.g., "client" -> "Household")
    const backendType = SEARCH_TYPE_TO_BACKEND_TYPE[item.type] ?? "Household"
    onSave({ type: backendType, id: item.id, label: item.display_label })
    setIsDialogOpen(false)
  }

  const handleClear = () => {
    setPendingValue(null)
    onSave(null)
  }

  return (
    <>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsDialogOpen(true)}
          className="w-full justify-start"
          disabled={isLoading}
        >
          {currentValue ? (
            <CrossReferenceChip
              value={{ type: currentValue.type!, id: currentValue.id!, label: currentValue.label }}
              size="sm"
              variant="pill"
            />
          ) : (
            <span className="text-muted-foreground">Select reference...</span>
          )}
        </Button>
        {currentValue && (
          <Button type="button" variant="outline" size="icon" onClick={handleClear} disabled={isLoading}>
            ×
          </Button>
        )}
        {isLoading && <LoaderCircle className="h-4 w-4 animate-spin" />}
      </div>

      <SearchDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSelect={handleSelect}
        title="Select Reference"
        placeholder={"Search..."}
        allowedTypes={allowedRelationshipSearchTypes(customField)}
        showPreview={true}
        widthVariant="compact"
      />
    </>
  )
}

interface UserReferenceFormFieldProps {
  customField: CustomFieldFormInputProps["customField"]
  value: string | null
  onSave: (value: string | null) => void
  isLoading: boolean
}

function UserReferenceFormField({ customField, value, onSave, isLoading }: UserReferenceFormFieldProps) {
  const { data: usersData } = useFetchUsers()

  const options = useMemo(() => {
    return (
      filterUsersForUserReferenceField(customField, usersData?.users).map((user) => ({
        value: user.id,
        label: user.full_name,
      })) || []
    )
  }, [customField, usersData?.users])

  const handleClear = () => {
    onSave(null)
  }

  return (
    <div className="flex gap-2">
      <Select value={value ?? ""} onValueChange={(val) => onSave(val || null)} disabled={isLoading}>
        <SelectTrigger className="w-full">
          {value ? <UserAvatar userId={value} size="sm" showName /> : <SelectValue placeholder="Select user..." />}
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              <UserAvatar userId={opt.value} size="sm" showName />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {value && (
        <Button type="button" variant="outline" size="icon" onClick={handleClear} disabled={isLoading}>
          ×
        </Button>
      )}
      {isLoading && <LoaderCircle className="h-4 w-4 animate-spin" />}
    </div>
  )
}

export function CustomFieldFormInput({ customField, value, onSave, isLoading }: CustomFieldFormInputProps) {
  switch (customField.field_type) {
    case "text":
      return (
        <TextInput
          id={`cf_${customField.field_key}`}
          value={(value as string) ?? ""}
          onChange={(e) => onSave(e.target.value || null)}
          placeholder={`Enter ${customField.display_name.toLowerCase()}`}
        />
      )

    case "rich_text": {
      const initialContent: LexicalText | undefined = value ? { html: String(value) } : undefined
      return (
        <Editor
          initialContent={initialContent}
          onChange={(lexicalText) => onSave(lexicalText.html || null)}
          startingPlaceholder={`Enter ${customField.display_name.toLowerCase()}`}
          disableHeadings
          disableSlashCommands
          styleOverrides={{
            "--lexical-editor-min-height": "60px",
            "--lexical-editor-max-height": "200px",
          }}
        />
      )
    }

    case "number":
      return (
        <TextInput
          id={`cf_${customField.field_key}`}
          type="number"
          value={(value as string) ?? ""}
          onChange={(e) => onSave(e.target.value === "" ? null : Number(e.target.value))}
          placeholder="0"
        />
      )

    case "date": {
      // Parse date string as local date to avoid timezone issues
      const parseLocalDate = (dateStr: string): Date | undefined => {
        if (!dateStr) return undefined
        const [year, month, day] = dateStr.split("-").map(Number)
        if (!year || !month || !day) return undefined
        return new Date(year, month - 1, day)
      }

      // Format date as YYYY-MM-DD using local timezone
      const formatLocalDate = (date: Date): string => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const day = String(date.getDate()).padStart(2, "0")
        return `${year}-${month}-${day}`
      }

      const dateValue = value ? parseLocalDate(String(value)) : undefined
      const placeholder = `Select ${customField.display_name.toLowerCase()}`

      return (
        <DateInput
          id={`cf_${customField.field_key}`}
          value={dateValue}
          onChange={(date) => onSave(date ? formatLocalDate(date) : null)}
          placeholder={placeholder}
        />
      )
    }

    case "boolean":
      return (
        <div className="flex h-9 items-center">
          <Switch checked={Boolean(value)} onCheckedChange={(checked) => onSave(checked)} disabled={isLoading} />
          {isLoading && <LoaderCircle className="ml-2 h-4 w-4 animate-spin" />}
        </div>
      )

    case "single_select":
      return (
        <Select value={(value as string) ?? ""} onValueChange={(val) => onSave(val || null)}>
          <SelectTrigger>
            <SelectValue placeholder={`Select ${customField.display_name.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {customField.options?.select_options?.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )

    case "multi_select":
      return (
        <MultiSelectInput
          id={`cf_${customField.field_key}`}
          values={Array.isArray(value) ? value : []}
          onChange={onSave}
          options={
            customField.options?.select_options?.map((option) => ({
              value: option,
              textLabel: option,
              label: (
                <span className="flex items-center gap-2">
                  <SelectOptionDot customField={customField} option={option} />
                  {option}
                </span>
              ),
            })) ?? []
          }
          placeholder={`Select ${customField.display_name.toLowerCase()}`}
          disabled={isLoading}
        />
      )

    case "url":
      return (
        <TextInput
          id={`cf_${customField.field_key}`}
          type="url"
          value={(value as string) ?? ""}
          onChange={(e) => onSave(e.target.value || null)}
          placeholder="https://example.com"
        />
      )

    case "cross_reference":
      return (
        <CrossReferenceFormField
          customField={customField}
          value={value as CrossReferenceValue}
          onSave={onSave}
          isLoading={isLoading}
        />
      )

    case "user_reference":
      return (
        <UserReferenceFormField
          customField={customField}
          value={value as string | null}
          onSave={onSave}
          isLoading={isLoading}
        />
      )

    default:
      return <span className="text-muted-foreground text-sm">Unsupported field type</span>
  }
}
